import { FC, useRef } from 'react';
import { Box, TextField, InputAdornment, IconButton } from '@mui/material';
import CalendarToday from '@mui/icons-material/CalendarToday';
import TimeHelper from '../../../utils/time';

interface FilterDateFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
}

const formatApiDateForDisplay = (apiDate: string): string => {
    const [year, month, day] = apiDate.split('-');
    if (!year || !month || !day) {
        return TimeHelper.formatDateWithDot(apiDate);
    }
    return TimeHelper.formatDateWithDot(new Date(Number(year), Number(month) - 1, Number(day)));
};

const openDatePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    if (typeof input.showPicker === 'function') {
        input.showPicker();
    } else {
        input.click();
    }
};

export const FilterDateField: FC<FilterDateFieldProps> = ({ label, value, onChange, onKeyDown }) => {
    const dateInputRef = useRef<HTMLInputElement>(null);

    return (
        <Box sx={{ position: 'relative', minWidth: 180 }}>
            <TextField
                size="small"
                label={label}
                value={formatApiDateForDisplay(value)}
                onClick={() => openDatePicker(dateInputRef.current)}
                onKeyDown={onKeyDown}
                slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                        readOnly: true,
                        sx: { cursor: 'pointer' },
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    edge="end"
                                    aria-label={label}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openDatePicker(dateInputRef.current);
                                    }}
                                >
                                    <CalendarToday fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{ minWidth: 180, width: '100%' }}
            />
            <input
                ref={dateInputRef}
                type="date"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                tabIndex={-1}
                aria-hidden
                style={{
                    position: 'absolute',
                    opacity: 0,
                    width: 0,
                    height: 0,
                    pointerEvents: 'none',
                }}
            />
        </Box>
    );
};
